/**
 * The `frappe` auth provider's gate: login page, or the app (Akhil, 2026-09-24).
 *
 * Mounted by `main.tsx` in place of the whole Clerk tree when `VITE_BOSUN_AUTH=frappe`. It asks
 * the site who it is — `auth.get_current_user`, authorised by the `sid` cookie — and `loginGate.ts`
 * turns that one answer into what to show. There is no client-side session flag to drift out of
 * step with the server, and a reload simply asks again.
 *
 * `checking` renders nothing rather than the login page: a password prompt flashed in front of
 * someone already signed in is worse than a blank instant.
 *
 * Sits OUTSIDE the app tree for the same reason `FrappeSessionGate` does — `FrappeProvider` opens
 * the realtime socket at mount, and that socket authenticates from the very cookie this gate is
 * about, so it must not open before there is one.
 */
import { useCurrentUser } from '../lib/api/useMasters'
import { loginGateState } from '../lib/loginGate'
import { FrappeLoginScreen } from './FrappeLoginScreen'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { registerCompanyPersister, restoreCompany } from '../lib/api/company'
import { registerMailboxPersister, restoreMyMailbox } from '../lib/api/mailbox'
import { readUserDefault, writeUserDefault } from '../lib/api/userDefaults'

/** The same two keys the Clerk bridge uses in `unsafeMetadata`, so one person's settings read the
 *  same whichever way they signed in. */
const COMPANY_DEFAULT_KEY = 'bosun_company'
const MAILBOX_DEFAULT_KEY = 'bosun_mailbox'
import { refreshIdentity } from '../lib/api/identityRefresh'

export function FrappeLoginGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const state = loginGateState({
    settled: !currentUser.isPending,
    user: currentUser.data ? { email: currentUser.data.email } : null,
    failed: currentUser.isError,
  })

  /*
    The two per-person settings Clerk's metadata held. With Clerk off they live on the Frappe user
    itself — `User.defaults`, Frappe's own per-user slot (`userDefaults.ts`) — restored once the
    session names a user, and written back whenever anything in the app changes them.
  */
  const email = currentUser.data?.email ?? ''
  useEffect(() => {
    if (email === '') return
    void readUserDefault(email, COMPANY_DEFAULT_KEY).then((company) => {
      if (company) restoreCompany(company)
    })
    void readUserDefault(email, MAILBOX_DEFAULT_KEY).then((address) => {
      if (address) restoreMyMailbox(address)
    })
    registerCompanyPersister((company) => void writeUserDefault(email, COMPANY_DEFAULT_KEY, company))
    registerMailboxPersister((address) => void writeUserDefault(email, MAILBOX_DEFAULT_KEY, address))
    return () => {
      registerCompanyPersister(null)
      registerMailboxPersister(null)
    }
  }, [email])

  if (state === 'checking') return null
  if (state === 'login') {
    return (
      <FrappeLoginScreen
        onSignedIn={() => {
          // The cookie is set. Everything this app knows about "who" comes from the server, so
          // the cache is dropped — a previous user's answers must not survive a sign-in — and the
          // identity is asked for again, which is what moves this gate to the app.
          void refreshIdentity(queryClient)
        }}
      />
    )
  }
  return <>{children}</>
}
